import React, { useEffect, useRef, useState } from 'react';
import ReelDownloadButton from '../../../studio/components/ReelDownloadButton';
import { isUrlExpired } from '../lib/isUrlExpired';
import type { RttsJobDetail } from '../types';

export type ReelPlayerProps = {
  job: RttsJobDetail;
  /** Re-fetch job to refresh signed_url after expiry. */
  onRefreshUrl: () => Promise<RttsJobDetail | null>;
  /** Poster shown until play (game thumbnail or reel-native). */
  posterUrl?: string | null;
  className?: string;
  children?: React.ReactNode;
};

function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Delivered reel player + download.
 * On signed URL expiry, re-fetches the job for a fresh URL.
 * Mobile/PWA: playsinline + webkit-playsinline, preload=none, fluid 16:9.
 * // BACKEND-DEP: confirm whether a dedicated URL-refresh endpoint exists; for now re-fetching the job is the MVP path.
 */
const ReelPlayer: React.FC<ReelPlayerProps> = ({
  job,
  onRefreshUrl,
  posterUrl,
  className,
  children,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [activeJob, setActiveJob] = useState(job);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    setActiveJob(job);
  }, [job]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // iOS Safari / standalone PWA: keep playback inline (not forced fullscreen).
    el.setAttribute('playsinline', 'true');
    el.setAttribute('webkit-playsinline', 'true');
  }, [activeJob.signed_url, posterUrl]);

  useEffect(() => {
    if (!isUrlExpired(activeJob.expires_at) && activeJob.signed_url) return;
    let cancelled = false;
    (async () => {
      setRefreshing(true);
      setRefreshError(null);
      try {
        const next = await onRefreshUrl();
        if (!cancelled && next) setActiveJob(next);
        if (!cancelled && (!next || !next.signed_url)) {
          setRefreshError('This reel link expired. Try refreshing.');
        }
      } catch {
        if (!cancelled) setRefreshError('Could not refresh the reel link.');
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeJob.expires_at, activeJob.signed_url, onRefreshUrl]);

  const durationLabel = formatDuration(activeJob.reel_duration_sec);
  const url = activeJob.signed_url || '';
  const poster = posterUrl?.trim() || undefined;

  return (
    <div
      className={['w-full max-w-full space-y-3', className].filter(Boolean).join(' ')}
      data-testid="rtts-reel-player"
    >
      {refreshing ? (
        <p className="text-xs text-secondary-400" data-testid="rtts-url-refreshing">
          Refreshing reel link…
        </p>
      ) : null}
      {refreshError ? (
        <p className="text-xs text-red-300" data-testid="rtts-url-refresh-error">
          {refreshError}
        </p>
      ) : null}

      {url ? (
        <video
          ref={videoRef}
          key={url}
          className="w-full max-w-full rounded-lg border border-secondary-700 bg-black aspect-video"
          controls
          playsInline
          preload="none"
          poster={poster}
          src={url}
          data-testid="rtts-video"
        >
          Your browser does not support video playback.
        </video>
      ) : (
        <div
          className="flex aspect-video w-full max-w-full items-center justify-center rounded-lg border border-secondary-700 bg-secondary-900 text-xs text-secondary-500"
          data-testid="rtts-video-missing"
        >
          Reel unavailable
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {url ? (
          <ReelDownloadButton
            url={url}
            className="btn-secondary text-xs inline-flex min-h-[44px] min-w-[44px] items-center justify-center px-3"
            testId="rtts-download"
          />
        ) : null}
        {durationLabel ? (
          <span className="text-xs text-secondary-500" data-testid="rtts-duration">
            {durationLabel}
          </span>
        ) : null}
      </div>

      {children}
    </div>
  );
};

export default ReelPlayer;
